// imports
import { FC, forwardRef, useImperativeHandle, useState } from 'react';
// components
import { InputBaseComponentProps, TextField, useTheme } from '@mui/material';
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';
// interfaces, constants, helper
import {
  StripeElement,
  StripeTextFieldProps,
  StripeTextFieldCVCProps,
  StripeTextFieldExpiryProps,
  StripeTextFieldNumberProps,
  Nullable,
} from '../../../interfaces';

const StripeInput = forwardRef<any, InputBaseComponentProps>(function StripeInput(props, ref) {
  const { component: Component, ...other } = props;
  const [mountNode, setMountNode] = useState<Nullable<any>>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => mountNode.focus(),
    }),
    [mountNode],
  );

  return <Component onReady={setMountNode} {...other} />;
});

export const StripeTextField: FC = <T extends StripeElement>(props: StripeTextFieldProps<T>) => {
  const theme = useTheme();
  const {
    error,
    helperText,
    inputProps,
    stripeElement,
    InputProps = {},
    InputLabelProps,
    labelErrorMessage,
    disabled,
    ...other
  } = props;

  return (
    <TextField
      fullWidth
      InputLabelProps={{
        ...InputLabelProps,
        shrink: true,
      }}
      error={error}
      InputProps={{
        ...InputProps,
        inputProps: {
          ...inputProps,
          ...InputProps.inputProps,
          component: stripeElement,
          options: {
            ...inputProps?.options,
            disabled: disabled,
            style: {
              base: {
                color: theme.palette.common.white,
                '::placeholder': {
                  color: theme.palette.customGrayColor.light,
                },
              },
            },
          },
        },
        inputComponent: StripeInput,
      }}
      helperText={error ? labelErrorMessage : helperText}
      {...(other as any)}
    />
  );
};

export const StripeTextFieldNumber: FC<StripeTextFieldNumberProps> = (props) => {
  return <StripeTextField stripeElement={CardNumberElement} disabled={props.disabled} {...props} />;
};

export const StripeTextFieldExpiry: FC<StripeTextFieldExpiryProps> = (props) => {
  return <StripeTextField stripeElement={CardExpiryElement} disabled={props.disabled} {...props} />;
};

export const StripeTextFieldCVC: FC<StripeTextFieldCVCProps> = (props) => (
  <StripeTextField stripeElement={CardCvcElement} disabled={props.disabled} {...props} />
);
